import React, { memo, useMemo } from 'react';

import Paragraph from '@/components/atomic/atoms/data-display/typography/paragraph/styled';
import FlexItem from '@/components/atomic/atoms/layout/flex-item';
import TopNavItemRenderer from '@/components/atomic/molecules/navigation/top-nav-items/item/renderer';
import type { TopNavigationItemPreloadedProps } from '@/components/atomic/molecules/navigation/top-nav-items/item/typings';
import ViewportRender from '@/components/atomic/molecules/utils/viewport/viewport-render';
import withExposer from '@/hocs/with-exposer';
import { LanguageSelectorProps } from '@/typings/components';
import { useTheme } from '@emotion/react';

const languageSelectorId = 'language-selector';

const TopNavItemLanguageSelectorComponent: React.FC<
	TopNavigationItemPreloadedProps
> = ({
	id,
	ariaLabel,
	label,
	link,
	fragmentVariationPath,
	dropdownSegment,
	model,
	$variant,
}) => {
	const { size, font } = useTheme();

	const activeLanguage = useMemo(() => {
		if (id !== languageSelectorId) return null;

		const firstItemKey = model?.[':itemsOrder']?.[0];
		if (!firstItemKey) return null;

		const root = model?.[':items']?.[firstItemKey];

		if (!root?.[':itemsOrder']) return null;
		if (root[':itemsOrder'].length > 0) {
			const key = root?.[':itemsOrder'][0];
			const value = root?.[':items']?.[key] as LanguageSelectorProps;
			const currentLanguage = value?.languageLinks?.find(
				(language) => language.defaultLanguage,
			);
			return currentLanguage;
		}
	}, [model, id]);

	const shortLabel = useMemo(() => {
		if (id !== languageSelectorId && !activeLanguage) return null;
		if (activeLanguage?.shortLabel) return activeLanguage.shortLabel;
		return null;
	}, [activeLanguage, id]);

	const mobileFormattedLabel = useMemo(() => {
		if (id !== languageSelectorId && !activeLanguage) return label;
		if (activeLanguage?.languageCode) return activeLanguage.mobileLabel;
		return label;
	}, [activeLanguage, label, id]);

	const formattedLabel = useMemo(() => {
		if (id !== languageSelectorId && !activeLanguage) return label;
		if (shortLabel) return shortLabel;
		return label;
	}, [activeLanguage, label, shortLabel, id]);

	const basicProps = useMemo(
		() => ({
			id,
			ariaLabel,
			label,
			link,
			fragmentVariationPath,
			dropdownSegment,
			$variant,
		}),
		[
			$variant,
			ariaLabel,
			dropdownSegment,
			fragmentVariationPath,
			id,
			label,
			link,
		],
	);

	return (
		<ViewportRender
			Component={TopNavItemRenderer}
			componentProps={{
				...basicProps,
				left: (
					<FlexItem
						$alignItems='center'
						$justifyContent='center'
						$width={24}
						$breakpoints={{ [size.laptop]: { $width: undefined } }}
					>
						<Paragraph
							as='i'
							$fontWeight={600}
							$lineHeight={'11px'}
							$fontSize={'11px'}
							$fontFamily={font.families.primary.bold}
							$fontStyle='normal'
							$textTransform='uppercase'
						>
							{shortLabel}
						</Paragraph>
					</FlexItem>
				),
				right: mobileFormattedLabel ? (
					<FlexItem $alignItems='center'>
						<span>{mobileFormattedLabel}</span>
					</FlexItem>
				) : null,
			}}
			$breakpoints={{
				[size.laptop]: {
					Component: TopNavItemRenderer,
					componentProps: {
						...basicProps,
						left: <React.Fragment />,
						right: label ? (
							<FlexItem $alignItems='center'>
								<span>{formattedLabel}</span>
							</FlexItem>
						) : null,
						$hasLabel: false,
					},
				},
			}}
		/>
	);
};

const TopNavItemLanguageSelector = memo(
	withExposer(
		TopNavItemLanguageSelectorComponent,
		'topNavItemLanguageSelector',
	),
);

export default TopNavItemLanguageSelector;
